import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setMainHeading } from '../../../../redux/modules/mainHeading/slice';
import {
    getAllRequest, updateAllRequest, resetUpdateRes, getRolesRequest,
    createRoleRequest, resetCreateRoleRes,
    deleteRoleRequest, resetDeleteRoleRes,
} from '../../../../redux/modules/permissions/slice';
import { toast } from 'react-toastify';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Checkbox,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    CircularProgress,
    Chip,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useNavigate } from 'react-router-dom';

const PROTECTED_ROLES = ['admin', 'config', 'patient', 'externalReferrer'];

const StyledToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    '& .MuiToggleButton-root': {
        padding: '2px 10px',
        fontSize: '0.7rem',
        fontWeight: 600,
        textTransform: 'none',
        border: `1px solid ${theme.palette.divider}`,
        '&.Mui-selected': {
            color: '#fff',
        },
        '&.Mui-selected.read': {
            backgroundColor: theme.palette.info.main,
            '&:hover': { backgroundColor: theme.palette.info.dark },
        },
        '&.Mui-selected.write': {
            backgroundColor: theme.palette.success.main,
            '&:hover': { backgroundColor: theme.palette.success.dark },
        },
    },
}));

const HeaderCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '0.8rem',
    textAlign: 'center',
    padding: '12px 8px',
    backgroundColor: theme.palette.grey[50],
    borderBottom: `2px solid ${theme.palette.divider}`,
    minWidth: 120,
}));

const ModuleCell = styled(TableCell)(({ theme }) => ({
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '10px 16px',
    backgroundColor: theme.palette.background.paper,
    borderRight: `1px solid ${theme.palette.divider}`,
    whiteSpace: 'nowrap',
}));

const PermCell = styled(TableCell)(({ theme }) => ({
    textAlign: 'center',
    padding: '6px 8px',
    borderRight: `1px solid ${theme.palette.grey[100]}`,
}));

export default function PermissionSettings() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        allPermissions, catalog, loading, updateLoading, updateRes, roles,
        createRoleLoading, createRoleRes, deleteRoleLoading, deleteRoleRes,
    } = useSelector(state => state.permissions);
    // Module rows + labels come from the server-served capability catalog —
    // the single source of truth, so adding a module never touches the client.
    const MODULE_KEYS = (catalog || []).map(c => c.module);
    const moduleLabel = (key) => (catalog || []).find(c => c.module === key)?.label || key;
    const moduleActions = (key) => (catalog || []).find(c => c.module === key)?.actions || ['read', 'write'];
    const currentUserRole = useSelector(state => state.auth.userData?.role);
    const [localPerms, setLocalPerms] = useState({});
    const [hasChanges, setHasChanges] = useState(false);

    // Add Role dialog
    const [addDialogOpen, setAddDialogOpen] = useState(false);
    const [newRoleName, setNewRoleName] = useState('');
    const [newDisplayText, setNewDisplayText] = useState('');

    // Delete Role dialog
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [roleToDelete, setRoleToDelete] = useState(null);

    // Roles that appear in the permissions grid (exclude externalReferrer — they have their own portal)
    const CONFIGURABLE_ROLES = (roles || []).filter(r => r.role !== 'externalReferrer');

    // A column is locked if it's admin, OR if the current user is config viewing their own role
    const isColumnLocked = (role) => role === 'admin' || (currentUserRole === 'config' && role === 'config');

    const isAdmin = currentUserRole === 'admin';

    useEffect(() => {
        dispatch(setMainHeading("Permissions"));
        dispatch(getAllRequest());
        dispatch(getRolesRequest());
    }, [dispatch]);

    // Build local state from fetched permissions
    useEffect(() => {
        if (allPermissions.length > 0) {
            const permMap = {};
            for (const doc of allPermissions) {
                const rolePerm = {};
                for (const p of doc.permissions) {
                    rolePerm[p.module] = { enabled: p.enabled, access: p.access };
                }
                permMap[doc.role] = rolePerm;
            }
            setLocalPerms(permMap);
            setHasChanges(false);
        }
    }, [allPermissions]);

    // Handle save response
    useEffect(() => {
        if (updateRes) {
            if (updateRes.success) {
                toast.success('Permissions saved successfully.');
                dispatch(getAllRequest());
            } else {
                toast.error(updateRes.message || 'Failed to save permissions. Please try again.');
            }
            dispatch(resetUpdateRes());
        }
    }, [updateRes, dispatch]);

    // Handle create role response
    useEffect(() => {
        if (createRoleRes) {
            if (createRoleRes.success) {
                toast.success('Role created successfully.');
                setAddDialogOpen(false);
                setNewRoleName('');
                setNewDisplayText('');
                dispatch(getRolesRequest());
                dispatch(getAllRequest());
            } else {
                toast.error(createRoleRes.message || 'Failed to create role. Please try again.');
            }
            dispatch(resetCreateRoleRes());
        }
    }, [createRoleRes, dispatch]);

    // Handle delete role response
    useEffect(() => {
        if (deleteRoleRes) {
            if (deleteRoleRes.success) {
                toast.success('Role deleted successfully.');
                setDeleteDialogOpen(false);
                setRoleToDelete(null);
                dispatch(getRolesRequest());
                dispatch(getAllRequest());
            } else {
                toast.error(deleteRoleRes.message || 'Failed to delete role. Please try again.');
            }
            dispatch(resetDeleteRoleRes());
        }
    }, [deleteRoleRes, dispatch]);

    const getPermission = useCallback((role, moduleKey) => {
        return localPerms[role]?.[moduleKey] || { enabled: false, access: 'read' };
    }, [localPerms]);

    const handleToggleEnabled = (role, moduleKey) => {
        if (isColumnLocked(role)) return;
        const actions = moduleActions(moduleKey);
        setLocalPerms(prev => {
            const current = prev[role]?.[moduleKey] || { enabled: false, access: 'read' };
            // Single-action modules (dashboard=read; finalize/email=write) have
            // no read/write choice — their access is fixed by the catalog.
            const access = actions.length === 1 ? actions[0] : current.access;
            return {
                ...prev,
                [role]: {
                    ...prev[role],
                    [moduleKey]: { ...current, enabled: !current.enabled, access },
                },
            };
        });
        setHasChanges(true);
    };

    const handleChangeAccess = (role, moduleKey, newAccess) => {
        if (isColumnLocked(role) || !newAccess) return;
        setLocalPerms(prev => {
            const current = prev[role]?.[moduleKey] || { enabled: false, access: 'read' };
            return {
                ...prev,
                [role]: {
                    ...prev[role],
                    [moduleKey]: { ...current, access: newAccess },
                },
            };
        });
        setHasChanges(true);
    };

    const handleSave = () => {
        const payload = [];
        for (const roleObj of CONFIGURABLE_ROLES) {
            if (roleObj.role === 'admin') continue;
            const rolePerms = localPerms[roleObj.role] || {};
            const permissions = MODULE_KEYS.map(mod => ({
                module: mod,
                enabled: rolePerms[mod]?.enabled || false,
                access: rolePerms[mod]?.access || 'read',
            }));
            payload.push({ role: roleObj.role, permissions });
        }
        dispatch(updateAllRequest(payload));
    };

    const handleReset = () => {
        dispatch(getAllRequest());
    };

    const handleCreateRole = () => {
        if (!newRoleName.trim() || !newDisplayText.trim()) return;
        dispatch(createRoleRequest({ roleName: newRoleName.trim(), displayText: newDisplayText.trim() }));
    };

    const handleDeleteRole = () => {
        if (!roleToDelete) return;
        dispatch(deleteRoleRequest({ role: roleToDelete }));
    };

    const openDeleteDialog = (role) => {
        setRoleToDelete(role);
        setDeleteDialogOpen(true);
    };

    const basePath = `/${useSelector(state => state.auth.userData?.role) || 'admin'}`;

    if (loading && allPermissions.length === 0) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 1200 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <IconButton
                    onClick={() => navigate(`${basePath}/settings`)}
                    size="small"
                    sx={{ mr: 1, color: 'text.secondary' }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" sx={{ fontWeight: 700, flex: 1 }}>
                    Role Permissions
                </Typography>
                {isAdmin && (
                    <Button
                        variant="outlined"
                        startIcon={<AddCircleOutlineIcon />}
                        onClick={() => setAddDialogOpen(true)}
                        sx={{ borderRadius: 2 }}
                        size="small"
                    >
                        Add Role
                    </Button>
                )}
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Control what each role can access. Tick to enable a module, then set read or write access.
                Admin always has full access.
            </Typography>

            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ borderRadius: 3, mb: 3, overflowX: 'auto' }}
            >
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <HeaderCell sx={{ textAlign: 'left', minWidth: 160, position: 'sticky', left: 0, zIndex: 3, backgroundColor: 'grey.50' }}>
                                Module
                            </HeaderCell>
                            {CONFIGURABLE_ROLES.map(roleObj => {
                                const locked = isColumnLocked(roleObj.role);
                                const lockLabel = roleObj.role === 'admin' ? 'Full Access' : 'Locked';
                                const canDelete = isAdmin && !PROTECTED_ROLES.includes(roleObj.role);
                                return (
                                    <HeaderCell key={roleObj.role} colSpan={1}>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Typography variant="caption" sx={{ fontWeight: 700, fontSize: '0.78rem' }}>
                                                    {roleObj.displayText}
                                                </Typography>
                                                {canDelete && (
                                                    <Tooltip title="Delete role">
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => openDeleteDialog(roleObj.role)}
                                                            sx={{ p: 0.25, color: 'text.disabled', '&:hover': { color: 'error.main' } }}
                                                        >
                                                            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                                                        </IconButton>
                                                    </Tooltip>
                                                )}
                                            </Box>
                                            {locked && (
                                                <Chip
                                                    icon={<LockOutlinedIcon sx={{ fontSize: 12 }} />}
                                                    label={lockLabel}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: '0.65rem' }}
                                                />
                                            )}
                                        </Box>
                                    </HeaderCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {MODULE_KEYS.map((moduleKey, idx) => (
                            <TableRow
                                key={moduleKey}
                                sx={{
                                    backgroundColor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                                    '&:hover': { backgroundColor: 'action.hover' },
                                }}
                            >
                                <ModuleCell sx={{ position: 'sticky', left: 0, zIndex: 1, backgroundColor: idx % 2 === 0 ? 'background.paper' : 'grey.50' }}>
                                    {moduleLabel(moduleKey)}
                                </ModuleCell>
                                {CONFIGURABLE_ROLES.map(roleObj => {
                                    const locked = isColumnLocked(roleObj.role);
                                    const perm = roleObj.role === 'admin'
                                        ? { enabled: true, access: 'write' }
                                        : getPermission(roleObj.role, moduleKey);
                                    const actions = moduleActions(moduleKey);
                                    const hasReadWriteChoice = actions.includes('read') && actions.includes('write');
                                    const tooltipText = locked
                                        ? (roleObj.role === 'admin' ? 'Admin always has access' : 'Cannot modify your own permissions')
                                        : (perm.enabled ? 'Disable' : 'Enable');

                                    return (
                                        <PermCell key={roleObj.role}>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                                                <Tooltip title={tooltipText}>
                                                    <span>
                                                        <Checkbox
                                                            checked={perm.enabled}
                                                            onChange={() => handleToggleEnabled(roleObj.role, moduleKey)}
                                                            disabled={locked}
                                                            size="small"
                                                            sx={{
                                                                p: 0.5,
                                                                '&.Mui-checked': { color: 'primary.main' },
                                                            }}
                                                        />
                                                    </span>
                                                </Tooltip>
                                                {hasReadWriteChoice ? (
                                                    <StyledToggleButtonGroup
                                                        value={perm.access}
                                                        exclusive
                                                        onChange={(e, val) => handleChangeAccess(roleObj.role, moduleKey, val)}
                                                        disabled={locked || !perm.enabled}
                                                        size="small"
                                                    >
                                                        <ToggleButton
                                                            value="read"
                                                            className={perm.access === 'read' ? 'read' : ''}
                                                        >
                                                            Read
                                                        </ToggleButton>
                                                        <ToggleButton
                                                            value="write"
                                                            className={perm.access === 'write' ? 'write' : ''}
                                                        >
                                                            Write
                                                        </ToggleButton>
                                                    </StyledToggleButtonGroup>
                                                ) : (
                                                    // Single-action module: show the fixed access (no choice).
                                                    <StyledToggleButtonGroup value={actions[0]} exclusive disabled size="small">
                                                        <ToggleButton value={actions[0]} className={actions[0]}>
                                                            {actions[0] === 'write' ? 'Write' : 'Read'}
                                                        </ToggleButton>
                                                    </StyledToggleButtonGroup>
                                                )}
                                            </Box>
                                        </PermCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    startIcon={<RestoreOutlinedIcon />}
                    onClick={handleReset}
                    disabled={!hasChanges || updateLoading}
                    sx={{ borderRadius: 2 }}
                >
                    Discard Changes
                </Button>
                <Button
                    variant="contained"
                    startIcon={updateLoading ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
                    onClick={handleSave}
                    disabled={!hasChanges || updateLoading}
                    sx={{ borderRadius: 2, px: 4 }}
                >
                    {updateLoading ? 'Saving...' : 'Save Permissions'}
                </Button>
            </Box>

            {/* Add Role Dialog */}
            <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Add New Role</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        label="Display Name"
                        placeholder="e.g. Lab Technician"
                        fullWidth
                        value={newDisplayText}
                        onChange={(e) => {
                            setNewDisplayText(e.target.value);
                            // Auto-generate camelCase role name from display text
                            const camel = e.target.value
                                .trim()
                                .split(/\s+/)
                                .map((w, i) => i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                                .join('');
                            setNewRoleName(camel);
                        }}
                        sx={{ mt: 1, mb: 2 }}
                    />
                    <TextField
                        label="Role Key"
                        placeholder="e.g. labTechnician"
                        fullWidth
                        value={newRoleName}
                        onChange={(e) => setNewRoleName(e.target.value)}
                        helperText="Internal identifier (auto-generated, camelCase)"
                        size="small"
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setAddDialogOpen(false)} disabled={createRoleLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleCreateRole}
                        disabled={!newRoleName.trim() || !newDisplayText.trim() || createRoleLoading}
                        sx={{ borderRadius: 2 }}
                    >
                        {createRoleLoading ? <CircularProgress size={20} color="inherit" /> : 'Create Role'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Role Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete Role</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the role <strong>{roleToDelete}</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This will fail if any users are still assigned to this role. Reassign them first.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteRoleLoading}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleDeleteRole}
                        disabled={deleteRoleLoading}
                        sx={{ borderRadius: 2 }}
                    >
                        {deleteRoleLoading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
